import type { RefObject } from "react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { ELLIPSIS_SLOT } from "./constants";

// Widths the fit is computed from, all in pixels
export type PaginationFitMetrics = {
  available: number;
  pageWidth: number;
  ellipsisWidth: number;
  gap: number;
};

type Measured = PaginationFitMetrics & {
  key: string;
  seenEllipsisWidth: number | null;
  natural: number;
};

type Measurement = {
  key: string;
  metrics: PaginationFitMetrics;
  compact: boolean;
};

type PaginationFitOptions = {
  hasNavigationButtons: boolean;
  resetKey: string;
};

type PaginationFit = {
  navRef: RefObject<HTMLElement | null>;
  listRef: RefObject<HTMLDivElement | null>;
  asideRef: RefObject<HTMLDivElement | null>;
  metrics: PaginationFitMetrics | null;
  compact: boolean;
};

function widestOf(items: HTMLElement[]): number {
  return items.reduce((widest, item) => Math.max(widest, item.offsetWidth), 0);
}

// Measures the room the nav is given and what an item costs in it
export function usePaginationFit({
  hasNavigationButtons,
  resetKey,
}: PaginationFitOptions): PaginationFit {
  const navRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const asideRef = useRef<HTMLDivElement | null>(null);
  const measuredRef = useRef<Measured | null>(null);

  const [measurement, setMeasurement] = useState<Measurement | null>(null);

  const measure = useCallback(() => {
    const nav = navRef.current;
    const list = listRef.current;

    if (!nav || !list || nav.clientWidth === 0) {
      return;
    }

    const items = Array.from(list.children) as HTMLElement[];

    const content = hasNavigationButtons ? items.slice(1, -1) : items;
    const navigation = hasNavigationButtons
      ? [items[0], items[items.length - 1]]
      : [];

    if (content.length === 0) {
      return;
    }

    const gap = Number.parseFloat(getComputedStyle(list).columnGap) || 0;

    const ellipses = content.filter(
      (item) => item.dataset.slot === ELLIPSIS_SLOT,
    );
    const pages = content.filter((item) => item.dataset.slot !== ELLIPSIS_SLOT);

    const pageWidth = widestOf(pages);

    if (pageWidth === 0) {
      return;
    }

    const previous =
      measuredRef.current?.key === resetKey ? measuredRef.current : null;

    const seenEllipsisWidth =
      ellipses.length > 0
        ? Math.max(previous?.seenEllipsisWidth ?? 0, widestOf(ellipses))
        : (previous?.seenEllipsisWidth ?? null);

    const aside = asideRef.current;

    const taken = aside
      ? aside.offsetWidth +
        (Number.parseFloat(getComputedStyle(nav).columnGap) || 0)
      : 0;

    const reserved =
      navigation.reduce((total, item) => total + item.offsetWidth, 0) +
      navigation.length * gap +
      taken;

    const next: Measured = {
      key: resetKey,
      available: nav.clientWidth - reserved,
      pageWidth: Math.max(previous?.pageWidth ?? 0, pageWidth),
      ellipsisWidth: seenEllipsisWidth ?? pageWidth,
      seenEllipsisWidth,
      natural: previous?.natural ?? list.scrollWidth,
      gap,
    };

    measuredRef.current = next;

    const metrics: PaginationFitMetrics = {
      available: next.available,
      pageWidth: next.pageWidth,
      ellipsisWidth: next.ellipsisWidth,
      gap: next.gap,
    };

    const compact = nav.clientWidth < next.natural;

    setMeasurement((current) =>
      current &&
      current.key === resetKey &&
      current.compact === compact &&
      current.metrics.available === metrics.available &&
      current.metrics.pageWidth === metrics.pageWidth &&
      current.metrics.ellipsisWidth === metrics.ellipsisWidth &&
      current.metrics.gap === metrics.gap
        ? current
        : { key: resetKey, metrics, compact },
    );
  }, [hasNavigationButtons, resetKey]);

  useLayoutEffect(measure);

  useEffect(() => {
    const nav = navRef.current;

    if (!nav) {
      return;
    }

    const observer = new ResizeObserver(measure);

    observer.observe(nav);

    return () => observer.disconnect();
  }, [measure]);

  const active = measurement?.key === resetKey ? measurement : null;

  return {
    navRef,
    listRef,
    asideRef,
    metrics: active?.metrics ?? null,
    compact: active?.compact ?? false,
  };
}
